/**
 * API Interceptor Service
 * Automatically attaches JWT tokens to API requests and handles auth errors
 */
import authService from './authService';
import { fetchFromApi as originalFetchFromApi } from '../utils/api';

class ApiInterceptor {
  constructor() {
    this.authErrorHandler = null;
    this.setupInterceptor();
  }
  /**
   * Enhanced fetch function that automatically handles authentication
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Fetch options
   * @returns {Promise<any>} - Response data
   */
  async interceptedFetch(endpoint, options = {}) {
    // Clone options to avoid mutating the original
    const enhancedOptions = { ...options };

    // Ensure headers object exists
    enhancedOptions.headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json, text/plain, */*',
      ...enhancedOptions.headers
    };

    // Add caller information for debugging
    enhancedOptions.caller = options.caller || 'apiInterceptor';

    // Auth is carried by the HttpOnly session cookie — fetchFromApi sets
    // credentials: 'include' so the browser attaches it automatically.

    try {
      const response = await originalFetchFromApi(endpoint, enhancedOptions);
      return response;
    } catch (error) {
      // Handle authentication errors
      if (this.isAuthError(error)) {
        await this.handleAuthError(error);
      }
      throw error;
    }
  }

  /**
   * Check if error is authentication-related (but not quota-related)
   * @param {Error} error - Error object
   * @returns {boolean} - True if auth error
   */
  isAuthError(error) {
    // First, check if error.data has quota-related information
    // This handles errors from our fetchFromApi function which sets error.data
    const errorData = error.data || error.body || (error.response && error.response.data);
    
    // Helper to check if data indicates a quota error
    const isQuotaData = (data) => {
      return data && (
        data.title === 'Plan Limit Exceeded' ||
        data.detail || // Include responses with detail field (e.g., plan not enabled messages)
        data.usageMetrics ||
        data.UsageMetrics || // Handle capital case from API
        data.limit !== undefined ||
        data.usage !== undefined
      );
    };
    
    // Get the status code from various possible locations
    const status = error.status || (error.response && error.response.status);
    
    // Check for 401 Unauthorized (always auth error)
    if (status === 401) return true;
    
    // For 403, check if it's quota-related or auth-related
    if (status === 403) {
      // If we have quota-related data, it's NOT an auth error
      if (isQuotaData(errorData)) {
        return false; // This is a quota error, not auth error
      }
      // Regular 403 without quota info is auth error
      return true;
    }

    return false;
  }
  /**
   * Handle authentication errors
   * @param {Error} error - Authentication error
   */
  async handleAuthError(error) {
    console.warn('Authentication error detected:', error);
    
    // Call custom auth error handler if set
    if (this.authErrorHandler) {
      try {
        await this.authErrorHandler(error);
        return;
      } catch (handlerError) {
        console.error('Auth error handler failed:', handlerError);
      }
    }
    
    // Default auth error handling
    // Clear stored auth data
    authService.clearAuth();
    
    // Only redirect to login if we're not already on a public/auth page.
    // Keep this in sync with AuthContext.publicPaths so unauthenticated probes
    // on these pages don't hijack the user mid-flow (e.g. /verify-email is hit
    // straight from the registration email and the user has no cookie yet).
    const currentPath = window.location.pathname;
    const authPaths = ['/', '/login', '/create-account', '/confirm-registration', '/verify-email', '/reset-password', '/pricing', '/unsubscribe'];
    const isAuthPath = authPaths.includes(currentPath) || currentPath.startsWith('/reset-password/');

    if (!isAuthPath) {
      // Redirect to login page
      window.location.href = '/login';
    }
  }
  /**
   * Setup the interceptor by replacing the global fetch function
   */
  setupInterceptor() {
    // We don't need to replace global fetch since we're using our own API utility
    // Instead, we'll export our intercepted function
  }

  /**
   * Set custom authentication error handler
   * @param {Function} handler - Custom error handler function
   */
  setAuthErrorHandler(handler) {
    this.authErrorHandler = handler;
  }
  /**
   * Get enhanced API utilities with authentication
   */
  getApiUtils() {
    return {
      get: async (endpoint, params = {}) => {
        const queryString = this.createQueryString(params);
        return this.interceptedFetch(`${endpoint}${queryString}`, { method: 'GET' });
      },

      post: async (endpoint, data) => {
        return this.interceptedFetch(endpoint, {
          method: 'POST',
          body: JSON.stringify(data)
        });
      },

      put: async (endpoint, data) => {
        return this.interceptedFetch(endpoint, {
          method: 'PUT',
          body: JSON.stringify(data)
        });
      },

      delete: async (endpoint) => {
        return this.interceptedFetch(endpoint, {
          method: 'DELETE'
        });
      }
    };
  }

  /**
   * Create query string from parameters
   * @param {Object} params - Query parameters
   * @returns {string} - Query string
   */
  createQueryString(params) {
    if (!params || Object.keys(params).length === 0) return '';
    
    const queryParams = [];
    
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined) continue;
      
      if (Array.isArray(value)) {
        value.forEach(item => {
          queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
        });
      } else {
        queryParams.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }
    
    return queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
  }
}

// Create singleton instance
const apiInterceptor = new ApiInterceptor();

// Export the enhanced API utilities
export const api = apiInterceptor.getApiUtils();
export const fetchFromApi = apiInterceptor.interceptedFetch.bind(apiInterceptor);
export const apiRequest = apiInterceptor.interceptedFetch.bind(apiInterceptor); // For compatibility
export const setAuthErrorHandler = apiInterceptor.setAuthErrorHandler.bind(apiInterceptor);

export default apiInterceptor;
